import { isEnvelope, type Channel, type EventEnvelope } from '@contracts/events';

export type ConnectionState = 'connecting' | 'open' | 'reconnecting' | 'closed';

export interface LiveSocketOptions {
  onEvent: (env: EventEnvelope) => void;
  onState?: (state: ConnectionState, attempt: number) => void;
  /** Called when a sequence gap shows events were missed; callers should re-sync via REST. */
  onGap?: (expected: number, received: number) => void;
  /** Server heartbeats arrive every 15 s; silence beyond this means a dead socket. */
  heartbeatTimeoutMs?: number;
  socketFactory?: (url: string) => WebSocket;
  url?: string;
}

const MAX_BACKOFF_MS = 30_000;

export function channelUrl(channel: Channel): string {
  const loc = window.location;
  const proto = loc.protocol === 'https:' ? 'wss' : 'ws';
  return `${proto}://${loc.host}/ws/${channel}`;
}

/** Reconnecting WebSocket with heartbeat watchdog and sequence-gap detection. */
export class LiveSocket {
  private ws: WebSocket | null = null;
  private attempt = 0;
  private lastSequence: number | null = null;
  private retryTimer: ReturnType<typeof setTimeout> | null = null;
  private watchdog: ReturnType<typeof setTimeout> | null = null;
  private stopped = false;
  state: ConnectionState = 'closed';

  constructor(
    private channel: Channel,
    private opts: LiveSocketOptions,
  ) {}

  connect(): void {
    this.stopped = false;
    this.open();
  }

  close(): void {
    this.stopped = true;
    this.clearTimers();
    if (this.ws) {
      this.ws.onclose = null;
      this.ws.close();
      this.ws = null;
    }
    this.setState('closed');
  }

  private setState(state: ConnectionState): void {
    this.state = state;
    this.opts.onState?.(state, this.attempt);
  }

  private open(): void {
    const url = this.opts.url ?? channelUrl(this.channel);
    this.setState(this.attempt === 0 ? 'connecting' : 'reconnecting');
    const ws = this.opts.socketFactory ? this.opts.socketFactory(url) : new WebSocket(url);
    this.ws = ws;
    ws.onopen = () => {
      this.attempt = 0;
      this.setState('open');
      this.armWatchdog();
    };
    ws.onmessage = (ev: MessageEvent) => this.handleMessage(ev.data);
    ws.onerror = () => {
      /* onclose follows and schedules the reconnect */
    };
    ws.onclose = () => {
      this.ws = null;
      if (!this.stopped) this.scheduleReconnect();
    };
  }

  private handleMessage(data: unknown): void {
    this.armWatchdog();
    if (typeof data !== 'string' || data.length > 1_000_000) return;
    let parsed: unknown;
    try {
      parsed = JSON.parse(data);
    } catch {
      return;
    }
    if (!isEnvelope(parsed)) return;
    const env = parsed;
    const isSnapshot = env.type.endsWith('.snapshot');
    if (isSnapshot) {
      this.lastSequence = env.sequence;
    } else if (env.type === 'heartbeat') {
      // Heartbeat carries the channel's latest sequence: a higher value means we missed events.
      if (this.lastSequence !== null && env.sequence > this.lastSequence) {
        this.opts.onGap?.(this.lastSequence + 1, env.sequence);
        this.lastSequence = env.sequence;
      }
    } else {
      if (this.lastSequence !== null && env.sequence > this.lastSequence + 1) {
        this.opts.onGap?.(this.lastSequence + 1, env.sequence);
      }
      if (this.lastSequence === null || env.sequence > this.lastSequence) this.lastSequence = env.sequence;
    }
    this.opts.onEvent(env);
  }

  /** Sends a latency acknowledgement for an event (used for end-to-end alert latency). */
  ack(env: EventEnvelope): void {
    if (this.ws && this.ws.readyState === 1) {
      this.ws.send(
        JSON.stringify({
          type: 'ack',
          eventId: env.eventId,
          publishedAt: env.occurredAt,
          clientReceivedAt: new Date().toISOString(),
        }),
      );
    }
  }

  private armWatchdog(): void {
    if (this.watchdog) clearTimeout(this.watchdog);
    this.watchdog = setTimeout(() => {
      // No message or heartbeat for too long: force a reconnect.
      this.ws?.close();
    }, this.opts.heartbeatTimeoutMs ?? 35_000);
  }

  private scheduleReconnect(): void {
    this.clearTimers();
    this.attempt += 1;
    this.setState('reconnecting');
    const ceiling = Math.min(MAX_BACKOFF_MS, 500 * 2 ** (this.attempt - 1));
    const delay = ceiling / 2 + Math.random() * (ceiling / 2);
    this.retryTimer = setTimeout(() => this.open(), delay);
  }

  private clearTimers(): void {
    if (this.retryTimer) clearTimeout(this.retryTimer);
    if (this.watchdog) clearTimeout(this.watchdog);
    this.retryTimer = null;
    this.watchdog = null;
  }
}
